/**
 * GoalsAdmin.tsx — manager-only panel for setting weekly goals per person
 * (ASM / TL / RM) plus a Company row at top. Since Oct 2, 2026 the last
 * column is the month's build goal (crew_goals period='month', metric='builds')
 * — the target the home-page build race measures against. 1-on-1 Mode shows
 * these goals read-only; this drawer is the one place they're set.
 *
 * Slides up over the Leaderboard, opened via the pencil button on the
 * Team Targets card. Saves on blur / Enter — no bulk "Save All" button,
 * every input is its own write.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  type CrewLeaderboardRow,
  type GoalMetricId,
  type WeeklyGoalsMap,
  currentWeekStart,
  fetchLeaderboard,
  fetchWeeklyGoalsMap,
  fetchCanvassers,
  setWeeklyGoal,
} from "./crewQueries";
import { weekRangeLabel } from "../../lib/weeklySpiffs";
import {
  fetchMonthBuildGoals, fetchMonthBuilds, isFirstMonday, monthName, monthStartOf, setMonthBuildGoal, todayET,
} from "../../lib/dailyCommitments";

// 4 weekly columns + the month's build goal (Oct 2, 2026). The month column is
// what the home-page build race measures against; 1-on-1 Mode reads it from here.
const GRID = "1fr repeat(4, 56px) 70px";

function prevMonthStart(ms: string): string {
  const [y, m] = ms.split("-").map(Number);
  return m === 1 ? `${y - 1}-12-01` : `${y}-${String(m - 1).padStart(2, "0")}-01`;
}

type Metric = GoalMetricId;
const METRICS: Array<{ id: Metric; label: string }> = [
  { id: "apps", label: "Apps" },
  { id: "claims", label: "Claims" },
  { id: "builds_scheduled", label: "Signed IA" },
  { id: "builds", label: "Builds" },
];

/** Sum every per-person target for a given metric. Company total is derived
 * from this so the modal stays in sync with what the leaderboard denominator
 * actually represents.
 */
function sumPerPerson(goals: WeeklyGoalsMap, metric: Metric, exclude?: Set<string>): number {
  let sum = 0;
  for (const [key, row] of Object.entries(goals)) {
    if (key === "company") continue;
    if (exclude && exclude.has(key)) continue; // canvassers don't roll into RM company totals
    const v = row?.[metric];
    if (typeof v === "number" && Number.isFinite(v) && v > 0) sum += v;
  }
  // Round to 2 decimals to match crew_goals.target numeric(14,2).
  return Math.round(sum * 100) / 100;
}

const ROLE_LABEL: Record<string, string> = {
  asm: "ASM",
  team_lead: "TL",
  restore_manager: "RM",
};

// Stable ordering so managers can scan the list the same way each time.
const ROLE_ORDER: Record<string, number> = {
  asm: 0,
  team_lead: 1,
  restore_manager: 2,
};

export default function GoalsAdmin({ onClose }: { onClose: () => void }) {
  const { member } = useAuth();
  const [rows, setRows] = useState<CrewLeaderboardRow[]>([]);
  const [goals, setGoals] = useState<WeeklyGoalsMap>({});
  const [canvassers, setCanvassers] = useState<Array<{ user_id: string; full_name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // Pinned for the life of the drawer so one left open across midnight ET
  // Sunday→Monday can't write half the edits to one week and half to the next.
  const weekStart = useMemo(() => currentWeekStart(), []);
  const monthStart = useMemo(() => monthStartOf(todayET()), []);
  const firstMonday = useMemo(() => isFirstMonday(todayET()), []);
  const [monthGoals, setMonthGoals] = useState<Record<string, number>>({});
  const [lastMonthBuilds, setLastMonthBuilds] = useState<Record<string, number>>({});
  // Canvasser ids are excluded from the RM/TL/RM company-total sum — they have
  // their own (apps-only) targets that don't feed the leaderboard denominator.
  // On-leave reps (college, etc.) are out of the goal grid and the company sum too.
  const [onLeaveIds, setOnLeaveIds] = useState<string[]>([]);
  const canvasserIdSet = useMemo(
    () => new Set([...canvassers.map((c) => c.user_id), ...onLeaveIds]),
    [canvassers, onLeaveIds],
  );
  // Ref mirrors the latest goals state so handleSave avoids the stale-closure
  // race when two per-person edits fire back-to-back. Without this, the second
  // save would compute its company sum from a `goals` snapshot that doesn't
  // yet include the first save's mutation, and the company mirror would lag.
  const goalsRef = useRef<WeeklyGoalsMap>(goals);
  useEffect(() => {
    goalsRef.current = goals;
  }, [goals]);
  // Company-row mirror writes run one after another, in the order the edits
  // happened, so two quick edits to the same metric can't land in the DB out
  // of order and leave the stored company total missing the last edit.
  const mirrorChain = useRef<Promise<void>>(Promise.resolve());
  function queueCompanyMirror(metric: Metric, target: number, createdBy: string) {
    mirrorChain.current = mirrorChain.current
      .then(() => setWeeklyGoal({ userId: null, metric, target, weekStart, createdBy }))
      .catch((e) => console.warn("[GoalsAdmin] company sync failed:", e));
  }

  async function reload() {
    try {
      setErr(null);
      const [lb, gm, cv, mg, lmb] = await Promise.all([
        fetchLeaderboard(),
        fetchWeeklyGoalsMap(weekStart),
        fetchCanvassers(),
        fetchMonthBuildGoals(monthStart),
        fetchMonthBuilds(prevMonthStart(monthStart)),
      ]);
      setRows(lb.filter((r) => !r.on_leave));
      setOnLeaveIds(lb.filter((r) => r.on_leave).map((r) => r.user_id));
      setGoals(gm);
      setCanvassers(cv);
      setMonthGoals(mg);
      setLastMonthBuilds(lmb);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sort people by role bucket then by name.
  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => {
        const ra = ROLE_ORDER[a.role] ?? 99;
        const rb = ROLE_ORDER[b.role] ?? 99;
        if (ra !== rb) return ra - rb;
        return a.full_name.localeCompare(b.full_name);
      }),
    [rows],
  );

  async function handleSave(
    userIdOrNull: string | null,
    metric: Metric,
    raw: string,
  ) {
    if (!member) return;
    const n = parseFloat(raw);
    // Allow decimals (split RM activity is 0.5, so .5 goals are sensible).
    // Round to 2 decimals to match crew_goals.target numeric(14,2).
    const target =
      Number.isFinite(n) && n > 0 ? Math.max(0, Math.round(n * 100) / 100) : 0;
    // optimistic — patch this slot in state, then re-derive company total.
    // Read from `goalsRef.current` (kept in sync via useEffect above) so rapid
    // back-to-back saves layer their mutations instead of stomping each other.
    const prev = goalsRef.current;
    const key = userIdOrNull ?? "company";
    const nextGoals: WeeklyGoalsMap = { ...prev, [key]: { ...(prev[key] ?? {}) } };
    if (target > 0) nextGoals[key]![metric] = target;
    else delete nextGoals[key]![metric];
    const newCompanyForMetric = sumPerPerson(nextGoals, metric, canvasserIdSet);
    nextGoals.company = { ...(nextGoals.company ?? {}) };
    if (newCompanyForMetric > 0) nextGoals.company[metric] = newCompanyForMetric;
    else delete nextGoals.company[metric];
    goalsRef.current = nextGoals; // sync ref BEFORE setState so the next handleSave call sees it
    setGoals(nextGoals);

    try {
      await setWeeklyGoal({
        userId: userIdOrNull,
        metric,
        target,
        weekStart,
        createdBy: member.id,
      });
      // Mirror the new company total into crew_goals so the Leaderboard's
      // Team Targets card (which reads crew_goals.scope='company') stays
      // honest. Queued in order rather than fired loose; the optimistic local
      // state already shows the right number.
      queueCompanyMirror(metric, newCompanyForMetric, member.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
      reload();
    }
  }

  async function handleSaveMonth(userId: string, raw: string) {
    if (!member) return;
    const n = parseFloat(raw);
    const target = Number.isFinite(n) && n > 0 ? Math.round(n * 2) / 2 : 0;
    setMonthGoals((prev) => {
      const next = { ...prev };
      if (target > 0) next[userId] = target; else delete next[userId];
      return next;
    });
    try {
      await setMonthBuildGoal(userId, monthStart, target, member.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
      reload();
    }
  }

  // Canvassers get an apps-only target. canvasserIdSet keeps it out of the
  // company total, so we only write the personal goal — no company mirror.
  async function handleSaveCanvasser(userId: string | null, _metric: Metric, raw: string) {
    if (!member || !userId) return;
    const n = parseFloat(raw);
    const target = Number.isFinite(n) && n > 0 ? Math.max(0, Math.round(n * 100) / 100) : 0;
    const prev = goalsRef.current;
    const nextGoals: WeeklyGoalsMap = { ...prev, [userId]: { ...(prev[userId] ?? {}) } };
    if (target > 0) nextGoals[userId]!.apps = target;
    else delete nextGoals[userId]!.apps;
    goalsRef.current = nextGoals;
    setGoals(nextGoals);
    try {
      await setWeeklyGoal({ userId, metric: "apps", target, weekStart, createdBy: member.id });
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Save failed");
      reload();
    }
  }

  /**
   * On initial load, force the company row in the DB to match sum-of-per-person.
   * Handles the bootstrap case where company was set manually before this
   * change shipped. Runs once when goals first finish loading; all 4 metrics
   * patched in a single setGoals call so React commits once.
   */
  useEffect(() => {
    if (loading || !member) return;
    const stored = goals.company ?? {};
    const drift: Array<{ metric: Metric; derived: number }> = [];
    for (const m of METRICS) {
      const derived = sumPerPerson(goals, m.id, canvasserIdSet);
      const old = stored[m.id] ?? 0;
      if (Math.abs(derived - old) > 0.001) drift.push({ metric: m.id, derived });
    }
    if (drift.length === 0) return;
    // Single optimistic state patch covering every drifted metric.
    const nextCompany = { ...stored };
    for (const d of drift) {
      if (d.derived > 0) nextCompany[d.metric] = d.derived;
      else delete nextCompany[d.metric];
    }
    const nextGoals = { ...goals, company: nextCompany };
    goalsRef.current = nextGoals;
    setGoals(nextGoals);
    // Persist each drifted metric through the same serialized mirror queue.
    for (const d of drift) queueCompanyMirror(d.metric, d.derived, member.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        background: "rgba(42,46,48,.45)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 700,
          maxHeight: "88vh",
          background: "#fff",
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Drawer header */}
        <div
          style={{
            padding: "14px 18px 12px",
            background: "var(--br-slate)",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: 9,
                fontWeight: 800,
                letterSpacing: ".18em",
                textTransform: "uppercase",
                color: "var(--br-peach)",
                lineHeight: 1,
              }}
            >
              Week of {weekRangeLabel(weekStart)}
            </div>
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontSize: 18,
                lineHeight: 1,
                textTransform: "uppercase",
                letterSpacing: "-0.01em",
                marginTop: 3,
              }}
            >
              Set Goals
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,.1)",
              color: "#fff",
              border: 0,
              borderRadius: 99,
              padding: "6px 12px",
              fontWeight: 800,
              fontSize: 11,
              letterSpacing: ".1em",
              textTransform: "uppercase",
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>

        {err && (
          <div
            style={{
              padding: "8px 14px",
              background: "#fff1e9",
              color: "var(--br-orange-ink)",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {err}
          </div>
        )}
        {loading && (
          <div style={{ padding: 20, color: "var(--fg3)", fontSize: 13 }}>
            Loading…
          </div>
        )}

        {!loading && (firstMonday || Object.keys(monthGoals).length === 0) && (
          <div
            style={{
              padding: "8px 14px",
              background: "#fff1e9",
              color: "var(--br-orange-ink)",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {firstMonday ? "First Monday of the month — " : ""}set each person's {monthName(monthStart)} build goal in the last column.
            It drives the build race on the home page. A build counts when Chad approves it and it's on the calendar.
          </div>
        )}

        <div style={{ overflowY: "auto", padding: "8px 10px 16px" }}>
          {/* Column header — 4 metric columns matching the data rows below.
              60px each lets "Signed IA" and 2-digit decimals fit comfortably. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: GRID,
              gap: 6,
              padding: "6px 8px",
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: ".12em",
              textTransform: "uppercase",
              color: "var(--fg3)",
            }}
          >
            <div>Name</div>
            {METRICS.map((m) => (
              <div key={m.id} style={{ textAlign: "center" }}>
                {m.label}
              </div>
            ))}
            <div style={{ textAlign: "center", color: "var(--br-orange)", lineHeight: 1.15 }}>
              {monthName(monthStart).slice(0, 3)}<br />builds
            </div>
          </div>

          {/* Company row — read-only, auto-derived from per-person sums. */}
          {!loading && (
            <CompanyTotalRow goals={goals} exclude={canvasserIdSet} monthTotal={Object.values(monthGoals).reduce((a, b) => a + b, 0)} />
          )}

          {/* Per-person rows, grouped by role */}
          {sorted.map((r, i) => {
            const showGroupHeader =
              i === 0 || sorted[i - 1]!.role !== r.role;
            return (
              <div key={r.user_id}>
                {showGroupHeader && (
                  <div
                    style={{
                      padding: "12px 8px 4px",
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: ".14em",
                      textTransform: "uppercase",
                      color: "var(--br-orange)",
                    }}
                  >
                    {ROLE_LABEL[r.role] ?? r.role}
                  </div>
                )}
                <GoalsRow
                  label={r.full_name}
                  sub={ROLE_LABEL[r.role] ?? r.role}
                  goalKey={r.user_id}
                  userIdOrNull={r.user_id}
                  goals={goals}
                  onSave={handleSave}
                  month={{ value: monthGoals[r.user_id], last: lastMonthBuilds[r.user_id] ?? 0, onCommit: (raw) => handleSaveMonth(r.user_id, raw) }}
                />
              </div>
            );
          })}

          {/* Canvassers — apps-only targets (Ship 5). Isolated from the
              company total via canvasserIdSet. */}
          {!loading && canvassers.length > 0 && (
            <>
              <div
                style={{
                  padding: "12px 8px 4px",
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  color: "var(--br-orange)",
                }}
              >
                Canvassers · apps only
              </div>
              {canvassers.map((c) => (
                <GoalsRow
                  key={c.user_id}
                  label={c.full_name}
                  sub="Canvasser"
                  goalKey={c.user_id}
                  userIdOrNull={c.user_id}
                  goals={goals}
                  onSave={handleSaveCanvasser}
                  appsOnly
                />
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function GoalsRow({
  label,
  sub,
  goalKey,
  userIdOrNull,
  goals,
  onSave,
  accent = "var(--fg1)",
  appsOnly = false,
  month,
}: {
  label: string;
  sub: string;
  goalKey: string;
  userIdOrNull: string | null;
  goals: WeeklyGoalsMap;
  onSave: (userId: string | null, metric: Metric, raw: string) => void;
  accent?: string;
  appsOnly?: boolean;
  month?: { value: number | undefined; last: number; onCommit: (raw: string) => void };
}) {
  const row = goals[goalKey] ?? {};
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: GRID,
        gap: 6,
        alignItems: "center",
        padding: "8px",
        borderBottom: "1px solid var(--line)",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 800,
            fontSize: 13,
            color: accent,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: ".08em",
            textTransform: "uppercase",
            color: "var(--fg3)",
          }}
        >
          {sub}
        </div>
      </div>
      {METRICS.map((m) =>
        appsOnly && m.id !== "apps" ? (
          <div
            key={m.id}
            style={{ textAlign: "center", color: "var(--fg3)", fontSize: 13, fontWeight: 700 }}
            title="Canvassers are tracked on apps only"
          >
            —
          </div>
        ) : (
          <GoalInput
            key={m.id}
            value={row[m.id]}
            onCommit={(raw) => onSave(userIdOrNull, m.id, raw)}
          />
        ),
      )}
      {month ? (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 2 }}>
          <GoalInput value={month.value} onCommit={month.onCommit} highlight />
          <div style={{ fontSize: 9, fontWeight: 700, color: "var(--fg3)", textAlign: "center" }} title="Builds credited last month">
            last mo {Number.isInteger(month.last) ? month.last : month.last.toFixed(1)}
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", color: "var(--fg3)", fontSize: 13, fontWeight: 700 }}>—</div>
      )}
    </div>
  );
}

/**
 * Read-only Company Total row. The 4 metric tiles render the live sum of
 * every per-person target in state — canvassers excluded, the same rule the
 * stored company row follows (they carry apps-only targets that don't feed
 * the leaderboard denominator), so what the drawer shows is what the board
 * shows. Sums update instantly as managers edit per-person numbers below.
 */
function CompanyTotalRow({ goals, exclude, monthTotal }: { goals: WeeklyGoalsMap; exclude: Set<string>; monthTotal: number }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: GRID,
        gap: 6,
        alignItems: "center",
        padding: "8px",
        borderBottom: "2px solid var(--line)",
        background: "rgba(244, 101, 48, 0.04)", // light orange wash
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 800,
            fontSize: 13,
            color: "var(--br-orange)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          Company total
        </div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: ".08em",
            textTransform: "uppercase",
            color: "var(--fg3)",
          }}
        >
          auto · sum of reps
        </div>
      </div>
      {METRICS.map((m) => {
        const v = sumPerPerson(goals, m.id, exclude);
        return (
          <div
            key={m.id}
            style={{
              width: "100%",
              textAlign: "center",
              border: "1px dashed var(--line)",
              borderRadius: 8,
              padding: "6px 6px",
              fontSize: 14,
              fontFamily: "var(--font-display)",
              letterSpacing: "-0.01em",
              color: "var(--fg1)",
              background: "rgba(255,255,255,.6)",
            }}
          >
            {v > 0 ? (Number.isInteger(v) ? String(v) : v.toFixed(1)) : "—"}
          </div>
        );
      })}
      <div
        style={{
          width: "100%",
          textAlign: "center",
          border: "1px dashed var(--br-orange)",
          borderRadius: 8,
          padding: "6px 6px",
          fontSize: 14,
          fontFamily: "var(--font-display)",
          color: "var(--fg1)",
          background: "rgba(255,255,255,.6)",
        }}
      >
        {monthTotal > 0 ? (Number.isInteger(monthTotal) ? String(monthTotal) : monthTotal.toFixed(1)) : "—"}
      </div>
    </div>
  );
}

function GoalInput({
  value,
  onCommit,
  highlight = false,
}: {
  value: number | undefined;
  onCommit: (raw: string) => void;
  highlight?: boolean;
}) {
  const [draft, setDraft] = useState(value != null ? String(value) : "");
  useEffect(() => {
    setDraft(value != null ? String(value) : "");
  }, [value]);

  return (
    <input
      type="number"
      inputMode="decimal"
      min={0}
      step="0.5"
      value={draft}
      placeholder="—"
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        if (draft !== (value != null ? String(value) : "")) onCommit(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      style={{
        width: "100%",
        textAlign: "center",
        border: highlight ? "1.5px solid var(--br-orange)" : "1px solid var(--line)",
        background: highlight ? "rgba(244, 101, 48, 0.05)" : undefined,
        borderRadius: 8,
        padding: "6px 6px",
        fontSize: 14,
        fontFamily: "var(--font-display)",
        letterSpacing: "-0.01em",
        color: "var(--fg1)",
        outline: "none",
      }}
    />
  );
}
